import { BoundingSphere, Tools } from '@babylonjs/core';
import { Animation } from '@babylonjs/core/Animations/animation';
import { EasingFunction, QuadraticEase } from '@babylonjs/core/Animations/easing';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { BoundingBox } from '@babylonjs/core/Culling/boundingBox';
import { Color4, Vector3 } from '@babylonjs/core/Maths/math';
import { Scene } from '@babylonjs/core/scene';

import { Base64Image } from '../../interfaces/Base64Image';
import ArticleNode from '../nodes/articleNode';
import Settings from '../settings/settings';
import { calculateScreenshotCameraDistance } from '../utils/cameraPositionCalculator';

export enum CameraAction {
  FOCUS = 'FOCUS',
  RECENTER = 'RECENTER',
  NONE = 'NONE',
}

export default class CameraController {
  public name: string;
  private scene: Scene;
  private camera: ArcRotateCamera;
  private screenshotCamera: ArcRotateCamera;
  private idleTimer: NodeJS.Timeout;

  constructor(scene: Scene) {
    this.scene = scene;
    this.setUpDefaultCamera();
    this.setUpScreenshotCamera();
  }

  private setUpDefaultCamera() {
    this.scene.createDefaultCamera(true, true, false);
    this.camera = this.scene.activeCamera as ArcRotateCamera;

    // allow mouse down event bubbling
    this.scene.preventDefaultOnPointerDown = false;
    this.camera.attachControl(this.scene.getEngine().getInputElement(), true);

    // Setup this.camera touch and mouse input behavior
    this.camera.pinchPrecision = 200 / this.camera.radius;
    this.camera.lowerRadiusLimit = 0.025;
    this.camera.upperBetaLimit = Math.PI / 2;
    this.camera.upperRadiusLimit = 800 * this.camera.radius;
    this.camera.wheelDeltaPercentage = 0.01;
    this.camera.pinchDeltaPercentage = 0.001;
    this.camera.speed = 10;
    this.camera.panningSensibility = 0;
    this.camera.radius = 400;
    this.camera.maxZ = 2500;
    this.camera.minZ = 10;
    this.camera.inputs.remove(this.camera.inputs.attached.keyboard);

    // Adjust camera movement behavior
    this.camera.useFramingBehavior = true;
    if (this.camera.framingBehavior) {
      this.camera.framingBehavior.framingTime = 0;
      this.camera.framingBehavior.elevationReturnTime = -1;
    }
  }

  private setUpScreenshotCamera() {
    this.screenshotCamera = new ArcRotateCamera(
      'screenshotCamera',
      (150 * Math.PI) / 180,
      (65 * Math.PI) / 180,
      350,
      null,
      this.scene,
      false,
    );
    this.screenshotCamera.layerMask = Settings.SCREENSHOT_CAMERA_VIEW_MASK;
  }

  public focusArticle(article: ArticleNode) {
    this.lookAtCenterOfBoundingBox(article.boundingBox);
  }

  public focusWholeConfiguration(root: ArticleNode) {
    const xOffset = Settings.IS_TABLET ? -325 : -275;
    const offset = new Vector3(xOffset, 100, 0);
    if (root && root.mesh) {
      const bounds = root.mesh.getHierarchyBoundingVectors(true);
      this.lookAtCenterOfBoundingBox(new BoundingBox(bounds.min, bounds.max), offset);
    }
  }

  private lookAtCenterOfBoundingBox(bounds: BoundingBox, cameraOffset?: Vector3) {
    this.scene.stopAllAnimations();
    const xOffset = Settings.IS_TABLET ? -300 : -200;
    const offset = cameraOffset !== undefined ? cameraOffset : new Vector3(xOffset, 0, 0);
    const newCamPosition = bounds.centerWorld.add(offset);
    const newCamTarget = bounds.centerWorld;

    const easingMode = new QuadraticEase();
    easingMode.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

    Animation.CreateAndStartAnimation(
      'pos',
      this.camera,
      'position',
      30,
      50,
      this.camera.position,
      newCamPosition,
      0,
      easingMode,
    );
    Animation.CreateAndStartAnimation(
      'target',
      this.camera,
      'target',
      30,
      50,
      this.camera.target,
      newCamTarget,
      0,
      easingMode,
    );
  }

  // only looks at the article without moving the camera
  public recenterArticle(article: ArticleNode) {
    this.scene.stopAllAnimations();
    const combinedBoundingBox = article.boundingBox;
    const easingMode = new QuadraticEase();
    easingMode.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

    Animation.CreateAndStartAnimation(
      'target',
      this.camera,
      'target',
      30,
      30,
      this.camera.target,
      combinedBoundingBox.centerWorld,
      0,
      easingMode,
    );
  }

  public startIntroAnimation(article: ArticleNode) {
    const mesh = article.mesh;
    // Animations for tilt and pan
    const alphaAnimation = new Animation(
      'rotation',
      'alpha',
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    const alphaKeys = [];
    alphaKeys.push({ frame: 0, value: 4.85 });
    alphaKeys.push({ frame: 120, value: 2.2 });
    alphaAnimation.setKeys(alphaKeys);

    const betaAnimation = new Animation(
      'rotation',
      'beta',
      30,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    const betaKeys = [];
    betaKeys.push({ frame: 0, value: 0 });
    betaKeys.push({ frame: 120, value: 1.3 });
    betaAnimation.setKeys(betaKeys);

    // Easing
    const easingMode = new QuadraticEase();
    easingMode.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
    alphaAnimation.setEasingFunction(easingMode);
    betaAnimation.setEasingFunction(easingMode);

    // Apply
    this.camera.animations = [];
    this.camera.animations.push(alphaAnimation);
    this.camera.animations.push(betaAnimation);

    // Start animation
    this.scene.beginAnimation(this.camera, 0, 120, false, 1);

    // panningSensibility has to be set to 0 after calling zoomOnMeshHierarchy() because that method resets its value
    this.camera.framingBehavior.zoomOnMeshHierarchy(mesh);
    this.camera.panningSensibility = 0;
  }

  private registerTurnTableAnimation() {
    this.camera.useAutoRotationBehavior = true;
    const autoRotationBehavior = this.camera.autoRotationBehavior;
    if (autoRotationBehavior) {
      autoRotationBehavior.idleRotationWaitTime = Settings.IDLE_ANIMATION_WAIT_TIME;
      autoRotationBehavior.zoomStopsAnimation = true;
    }
  }

  private clearIdleTimeout() {
    this.idleTimer && clearTimeout(this.idleTimer);
  }

  public enableIdleAnimation() {
    this.clearIdleTimeout();
    this.idleTimer = setTimeout(
      () => this.registerTurnTableAnimation(),
      Settings.IDLE_ANIMATION_WAIT_TIME,
    );
  }

  public disableIdleAnimation() {
    this.clearIdleTimeout();
    this.camera.useAutoRotationBehavior = false;
  }

  public createScreenshot(root: ArticleNode): Promise<Base64Image> {
    const { min, max } = root.mesh.getHierarchyBoundingVectors(true);
    const boundingSphere = new BoundingSphere(min, max);
    this.screenshotCamera.setTarget(boundingSphere.center);

    this.screenshotCamera.radius = calculateScreenshotCameraDistance(
      boundingSphere.radiusWorld,
      this.screenshotCamera.fov,
    );

    return Tools.CreateScreenshotUsingRenderTargetAsync(
      this.scene.getEngine(),
      this.screenshotCamera,
      2500,
    );
  }

  public createTemplateScreenshot(root: ArticleNode) {
    // make transparent background
    const oldColor = this.scene.clearColor;
    this.scene.clearColor = new Color4(0, 0, 0, 0);

    // hide the ground
    const ground1 = this.scene.getMeshByName('outerCircle');
    const ground2 = this.scene.getMeshByName('innerCircle');
    ground1.setEnabled(false);
    ground2.setEnabled(false);

    // position the camera
    const { min, max } = root.mesh.getHierarchyBoundingVectors(true);
    const boundingSphere = new BoundingSphere(min, max);
    this.screenshotCamera.setTarget(boundingSphere.center);

    this.screenshotCamera.radius = calculateScreenshotCameraDistance(
      boundingSphere.radiusWorld,
      this.screenshotCamera.fov,
    );
    // adjust distance if needed to get perfect screenshot
    // this.screenshotCamera.radius -= 100

    this.screenshotCamera.alpha += (70 * Math.PI) / 180;

    // take screenshot
    Tools.CreateScreenshotUsingRenderTarget(this.scene.getEngine(), this.screenshotCamera, {
      width: 2000,
      height: 2000,
    });

    // restore
    this.scene.clearColor = oldColor;
    this.screenshotCamera.alpha -= (70 * Math.PI) / 180;
    ground1.setEnabled(true);
    ground2.setEnabled(true);
  }

  public zoomIn(): void {
    this.camera.inertialRadiusOffset += Settings.BUTTON_ZOOM_FACTOR;
  }

  public zoomOut(): void {
    this.camera.inertialRadiusOffset -= Settings.BUTTON_ZOOM_FACTOR;
  }
}
